import { flattenCategoryTreeV2 } from "@osmix/catalog-core/category-tree";
import { sleep } from "@/lib/utils";

import type {
	Api,
	CommissionCategoryRow,
	CommissionHistoryEntry,
	CommissionShopCategory,
	CommissionSource,
} from "../types";

// Офлайн-зеркало раздела «Комиссии»: та же логика приоритета, что в шлюзе
// (services/commission.ts), на небольшом демо-наборе категорий и магазинов.

type Node = { id: string; name: string; parentId: string | null };

// Дерево v2 (миграция 134) — тот же источник, что сид шлюза.
const CATEGORIES: Node[] = flattenCategoryTreeV2().map((n) => ({ id: n.id, name: n.name, parentId: n.parentId }));

type Shop = {
	id: string;
	name: string;
	inn: string;
	status: string;
	/** categoryId → [товаров, оборот за 30 дней]. */
	cells: Record<string, [number, number]>;
};

const SHOPS: Shop[] = [
	{ id: "shop-elina", name: "Футболки Элины", inn: "772312345678", status: "active", cells: { "cat-w-tshirts": [8, 84_000], "cat-m-tshirts": [5, 31_000], "cat-w-sneakers": [3, 12_500] } },
	{ id: "shop-lina", name: "Мастерская Лины", inn: "231512345690", status: "active", cells: { "cat-1-1": [6, 46_200], "cat-1-2": [4, 18_900] } },
	{ id: "shop-keramika", name: "Гончарная мастерская", inn: "770987654321", status: "active", cells: { "cat-7-1": [21, 128_400] } },
	{ id: "shop-uyut", name: "Уютный дом", inn: "526012345682", status: "active", cells: { "cat-7-3": [12, 22_000], "cat-7-1": [9, 40_300] } },
	{ id: "shop-silver-moon", name: "Silver Moon", inn: "7801234561", status: "blocked", cells: { "cat-w-jewelry": [7, 0] } },
];

let baseRate = 0.12;
// Рекомендованные стартовые ставки v2 (коридор 10-20 %).
const byCategory: Record<string, number> = Object.fromEntries(
	flattenCategoryTreeV2()
		.filter((n) => n.rate != null)
		.map((n) => [n.id, n.rate as number]),
);
const byShop: Record<string, number> = { "shop-keramika": 0.07 };
const byShopCategory: Record<string, Record<string, number>> = {
	"shop-elina": { "cat-1": 0.06, "cat-2": 0.07 },
};
const history: CommissionHistoryEntry[] = [];

const parentOf = new Map(CATEGORIES.map((c) => [c.id, c.parentId]));
const nameOf = new Map(CATEGORIES.map((c) => [c.id, c.name]));
const lineage = (id: string): string[] => {
	const out: string[] = [];
	let current: string | null = id;
	while (current && !out.includes(current)) {
		out.push(current);
		current = parentOf.get(current) ?? null;
	}
	return out;
};
const path = (id: string) =>
	lineage(id)
		.reverse()
		.map((c) => nameOf.get(c) ?? c)
		.join(" / ");

const resolve = (
	shopId: string,
	categoryId: string,
): { rate: number; source: CommissionSource; categoryId?: string } => {
	for (const id of lineage(categoryId)) {
		const rate = byShopCategory[shopId]?.[id];
		if (rate != null) return { rate, source: "shop_category", categoryId: id };
	}
	const own = byShop[shopId];
	if (own != null) return { rate: own, source: "shop" };
	for (const id of lineage(categoryId)) {
		const rate = byCategory[id];
		if (rate != null) return { rate, source: "category", categoryId: id };
	}
	return { rate: baseRate, source: "base" };
};

const window = (days = 30) => {
	const to = new Date();
	return { days, from: new Date(to.getTime() - days * 86_400_000).toISOString(), to: to.toISOString() };
};

const ordered = (): { node: Node; depth: number }[] => {
	const out: { node: Node; depth: number }[] = [];
	const walk = (parent: string | null, depth: number) => {
		for (const node of CATEGORIES.filter((c) => c.parentId === parent)) {
			out.push({ node, depth });
			walk(node.id, depth + 1);
		}
	};
	walk(null, 0);
	return out;
};

const categoryRow = (node: Node, depth: number): CommissionCategoryRow => {
	const resolved = resolve("", node.id);
	let shopCount = 0;
	let productCount = 0;
	let sales = 0;
	for (const shop of SHOPS) {
		let inside = false;
		for (const [id, [products, amount]] of Object.entries(shop.cells)) {
			if (!lineage(id).includes(node.id)) continue;
			inside = true;
			productCount += products;
			sales += amount;
		}
		if (inside) shopCount++;
	}
	return {
		id: node.id,
		name: node.name,
		parentId: node.parentId,
		depth,
		sortOrder: 0,
		rate: byCategory[node.id] ?? null,
		effectiveRate: resolved.rate,
		inheritedFromId:
			byCategory[node.id] == null && resolved.source === "category" ? (resolved.categoryId ?? null) : null,
		shopCount,
		personalCount: Object.values(byShopCategory).filter((rates) => rates[node.id] != null).length,
		productCount,
		sales,
	};
};

const shopCategories = (shop: Shop): CommissionShopCategory[] =>
	[...new Set([...Object.keys(shop.cells), ...Object.keys(byShopCategory[shop.id] ?? {})])]
		.map((categoryId) => {
			const resolved = resolve(shop.id, categoryId);
			return {
				categoryId,
				name: nameOf.get(categoryId) ?? categoryId,
				path: path(categoryId),
				productCount: shop.cells[categoryId]?.[0] ?? 0,
				sales: shop.cells[categoryId]?.[1] ?? 0,
				rate: byShopCategory[shop.id]?.[categoryId] ?? null,
				effectiveRate: resolved.rate,
				source: resolved.source,
				sourceCategoryId: resolved.categoryId ?? null,
			};
		})
		.sort((a, b) => a.path.localeCompare(b.path, "ru"));

const write = (shopId: string | null, categoryId: string | null, rate: number | null): boolean => {
	const before =
		shopId && categoryId
			? byShopCategory[shopId]?.[categoryId]
			: shopId
				? byShop[shopId]
				: categoryId
					? byCategory[categoryId]
					: undefined;
	if ((before ?? null) === rate) return false;
	if (shopId && categoryId) {
		const rates = (byShopCategory[shopId] ??= {});
		if (rate == null) delete rates[categoryId];
		else rates[categoryId] = rate;
	} else if (shopId) {
		if (rate == null) delete byShop[shopId];
		else byShop[shopId] = rate;
	} else if (categoryId) {
		if (rate == null) delete byCategory[categoryId];
		else byCategory[categoryId] = rate;
	}
	history.unshift({
		id: `h_${history.length + 1}`,
		createdAt: new Date().toISOString(),
		actorName: "Демо",
		scope: shopId && categoryId ? "shop_category" : shopId ? "shop" : "category",
		shopId,
		shopName: SHOPS.find((s) => s.id === shopId)?.name ?? null,
		categoryId,
		categoryName: categoryId ? (nameOf.get(categoryId) ?? null) : null,
		fromRate: before ?? null,
		toRate: rate,
	});
	return true;
};

export const commissions: Api["commissions"] = {
	async overview(days) {
		await sleep(120);
		return {
			baseRate,
			window: window(days),
			categories: ordered().map(({ node, depth }) => categoryRow(node, depth)),
			shopsWithOwnRate: Object.keys(byShop).length,
			personalTotal: Object.values(byShopCategory).reduce((n, r) => n + Object.keys(r).length, 0),
		};
	},
	async shops(days) {
		await sleep(120);
		return {
			baseRate,
			window: window(days),
			shops: SHOPS.map((shop) => {
				const cells = Object.values(shop.cells);
				return {
					id: shop.id,
					name: shop.name,
					slug: shop.id,
					inn: shop.inn,
					status: shop.status,
					productCount: cells.reduce((n, [p]) => n + p, 0),
					sales: cells.reduce((n, [, s]) => n + s, 0),
					shopRate: byShop[shop.id] ?? null,
					personalCount: Object.keys(byShopCategory[shop.id] ?? {}).length,
					categories: shopCategories(shop),
				};
			}).sort((a, b) => b.sales - a.sales),
		};
	},
	async categoryShops(categoryId, days) {
		await sleep(120);
		const entry = ordered().find(({ node }) => node.id === categoryId);
		if (!entry) throw new Error("Категория не найдена");
		const shops = SHOPS.map((shop) => {
			let productCount = 0;
			let sales = 0;
			for (const [id, [p, s]] of Object.entries(shop.cells)) {
				if (!lineage(id).includes(categoryId)) continue;
				productCount += p;
				sales += s;
			}
			const rate = byShopCategory[shop.id]?.[categoryId] ?? null;
			if (productCount === 0 && sales === 0 && rate == null) return null;
			const resolved = resolve(shop.id, categoryId);
			return {
				shopId: shop.id,
				name: shop.name,
				slug: shop.id,
				inn: shop.inn,
				status: shop.status,
				productCount,
				sales,
				rate,
				shopRate: byShop[shop.id] ?? null,
				effectiveRate: resolved.rate,
				source: resolved.source,
				sourceCategoryId: resolved.categoryId ?? null,
			};
		}).filter((row) => row !== null);
		return { category: categoryRow(entry.node, entry.depth), window: window(days), shops };
	},
	async history(filter = {}) {
		await sleep(80);
		return history
			.filter((row) => !filter.shopId || row.shopId === filter.shopId)
			.filter((row) => !filter.categoryId || row.categoryId === filter.categoryId)
			.slice(0, filter.limit ?? 50);
	},
	async setBase(rate) {
		await sleep(120);
		if (rate !== baseRate) {
			history.unshift({
				id: `h_${history.length + 1}`,
				createdAt: new Date().toISOString(),
				actorName: "Демо",
				scope: "base",
				shopId: null,
				shopName: null,
				categoryId: null,
				categoryName: null,
				fromRate: baseRate,
				toRate: rate,
			});
		}
		baseRate = rate;
		return { baseRate };
	},
	async setRate(change) {
		await sleep(120);
		return { changed: write(change.shopId ?? null, change.categoryId ?? null, change.rate) ? 1 : 0 };
	},
	async bulk(input) {
		await sleep(150);
		let changed = 0;
		for (const shopId of input.shopIds) {
			if (write(shopId, input.categoryId, input.rate)) changed++;
			if (!input.categoryId && input.resetPersonal) {
				for (const categoryId of Object.keys(byShopCategory[shopId] ?? {})) {
					if (write(shopId, categoryId, null)) changed++;
				}
			}
		}
		return { changed };
	},
};
